<template>
  <div class="tip" :class="{ 'is-new': isNewTip }">
    <div class="tip-wrapper" :class="{ 'add-new': isNewTip }">
      <!-- Lightbulb icon (drag handle for existing tips) -->
      <svg
        :class="['tip-icon', { 'drag-handle': !isNewTip }]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2">
        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
      </svg>

      <!-- Tip input -->
      <input
        v-if="isNewTip"
        type="text"
        v-model.trim="tip.value"
        class="tip-input"
        placeholder="Add a tip..."
        @keyup.enter="addTip"
      />
      <editor
        v-else
        class="tip-editor"
        v-model.trim="tip.value"
        placeholder="e.g. leave the onions longer if you like them burnt"
        @input="updateTip"
      />

      <!-- Action buttons -->
      <button
        v-if="isNewTip && tip.value"
        class="tip-action-btn add"
        @click="addTip"
        title="Add tip">
        <b-icon icon="plus" size="is-small" />
      </button>
      <button
        v-if="!isNewTip"
        class="tip-action-btn "
        @click="removeTip"
        title="Delete">
        <b-icon icon="delete" size="is-small" />
      </button>
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import Editor from "@/components/BaseEditor"
import debounce from "lodash/debounce"

export default {
  name: 'EditTip',
  components: {
    Editor,
  },
  props: [
    "tipIn",
    "isNewTip",
    "isSmall"
  ],
  data() {
    return {
      tip: null,
    }
  },
  created() {
    this.tip = this.tipIn ?? { id: null, value: null }
  },
  computed: {
  },
  methods: {
    addTip() {
      if (!this.tip.value || !this.tip.value.trim()) return;
      this.tip.id = uuidv4();
      // Wrap plain text in <p> tag so it renders correctly in the editor
      if (!this.tip.value.startsWith('<')) {
        this.tip.value = `<p>${this.tip.value}</p>`;
      }
      this.$emit('add-tip', this.tip);
      this.tip = {id: null, value: null};
    },
    removeTip() {
      this.$emit('remove-tip', this.tip);
    },
    updateTip: debounce(function () {
      this.$emit('update-tip', this.tip);
    }, 1000)
  }
}
</script>

<style scoped>
.tip {
  margin-bottom: 8px;
  border: none;
}

.tip:last-child {
  margin-bottom: 0;
}

.tip-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.tip-wrapper.add-new {
  margin-top: 8px;
  padding: 8px 10px;
  border: 1px dashed var(--border-light);
  border-radius: var(--radius-sm);
}

.tip-wrapper.add-new:focus-within {
  border-style: solid;
  border-color: var(--feeds-orange);
  background: #fff8f5;
}

.tip-icon {
  width: 18px;
  height: 18px;
  color: var(--feeds-orange);
  flex-shrink: 0;
}

.tip-icon.drag-handle {
  cursor: grab;
}

.tip-icon.drag-handle:active {
  cursor: grabbing;
}

.tip-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-family: inherit;
  color: var(--text-primary);
}

.tip-input::placeholder {
  color: var(--text-muted);
  font-style: italic;
}

.tip-editor {
  flex: 1;
  min-width: 0;
}

.tip-editor :deep(.ProseMirror) {
  border: none;
  padding: 0;
  min-height: 18px;
  font-size: 13px;
  background: transparent;
  box-shadow: none;
}

.tip-editor :deep(.ProseMirror:focus) {
  box-shadow: none;
  border: none;
}

.tip-action-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
  flex-shrink: 0;
}

.tip-action-btn.delete:hover {
  background: #fef2f2;
  color: #dc2626;
}

.tip-action-btn.add:hover {
  background: var(--feeds-green-light);
  color: var(--feeds-green);
}
</style>
